import {
  AutorenewRounded as OngoingIcon,
  EventOutlined as EventOutlinedIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { Chip, MiniCard, Modal, SearchField } from '@sb/cc-design-system';
import dayjs from 'dayjs';
import { ChangeEvent, KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import ClickPromptIcon from 'src/assets/images/ClickPromptWithLeftArrow.svg';
import NotEditableIcon from 'src/assets/images/NotEditable.svg';
import NoValidPromoIcon from 'src/assets/images/NoValidPromo.svg';
import { EmptyProvider } from 'src/components/emptyProvider';
import { GcfComponentSection } from 'src/components/gcfComponentSection';
import { Progress } from 'src/components/progress/Progress';
import { US_DATE_SLASH_FORMAT } from 'src/constants';
import { useResizeObserver } from 'src/hooks';
import { ComponentTypes, IComponent } from 'src/interfaces';
import { lang } from 'src/utils';
import { getSectionPreviewData } from 'src/utils/gcf-helper';

import { isAllowedToAddOverride } from '../overrideLeftSideBar/OverrideLeftSideBar.helper';
import { IOverrideRightSideBar } from './OverrideRightSideBar.interface';
import {
  ExpandedComponentModalContent,
  ExpandedComponentPreviewInner,
  ExpandedComponentPreviewWrapper,
  ExpandedComponentTags,
  InitialLoadingWrapper,
  InViewTrigger,
  LoadingMoreWrapper,
  MatchedComponentsList,
  MiniCardImageSlotPlaceholder,
  MiniCardImageSlotPlaceholderText,
  MiniCardWrapper,
  NotEditableImage,
  NotEditableState,
  OverrideRightSideBarContentWrapper,
  OverrideRightSideBarHeader,
  OverrideRightSideBarWrapper,
  SearchFieldWrapper,
  StyledEmptyProviderMessage,
  StyledNotEditableProviderMessage,
} from './OverrideRightSideBar.styled';

const SEARCH_RESET_DEBOUNCE_MS = 500;

const formatMiniCardDate = (component: IComponent) => {
  return component.end_date
    ? dayjs(component.end_date).format(US_DATE_SLASH_FORMAT)
    : lang.scheduleComponent.preview.ongoing;
};

const getMiniCardSubLabelIcon = (component: IComponent) =>
  component.end_date ? <EventOutlinedIcon /> : <OngoingIcon />;

const getComponentChipLabels = (component: IComponent) =>
  component.componentTags?.map((tag) => tag.name) || [];

const renderMiniCardImageSlot = () => (
  <MiniCardImageSlotPlaceholder>
    <MiniCardImageSlotPlaceholderText variant="caption">
      {lang.scheduleComponent.preview.previewComingSoon}
    </MiniCardImageSlotPlaceholderText>
  </MiniCardImageSlotPlaceholder>
);

const ExpandedComponentPreview = ({ component }: { component: IComponent }) => {
  const { containerRef, ...containerSize } = useResizeObserver();

  return (
    <ExpandedComponentPreviewWrapper>
      <ExpandedComponentPreviewInner ref={containerRef}>
        <GcfComponentSection
          fullWidth
          isFromSidebar
          assemblyComponents={[
            {
              component: {
                type: component.component_type?.name,
                config: component.config,
                name: component.name,
                width: component.width,
                height: component.height,
                theme: component.theme?.name,
              },
            },
          ]}
          height={component.height || 0}
          width={component.width || 0}
          containerSize={containerSize}
          previewData={getSectionPreviewData(
            component.component_type?.name as ComponentTypes,
          )}
        />
      </ExpandedComponentPreviewInner>
    </ExpandedComponentPreviewWrapper>
  );
};

export const OverrideRightSideBar = ({
  selectedSectionDetailInfo,
  hasClickedAddOverride = false,
  matchedComponents,
  isMatchedComponentsLoading,
  showMatchedComponentsLoader = false,
  hasNextMatchedComponentsPage,
  fetchNextMatchedComponentsPage,
  isFetchingNextMatchedComponentsPage,
  onSearchKeywordChange: onSearchKeywordChangeProp,
  onSelectPromo,
}: IOverrideRightSideBar) => {
  const [searchKeyword, setSearchKeyword] = useState('');
  const [expandedComponent, setExpandedComponent] = useState<IComponent | null>(
    null,
  );
  const lastSubmittedSearchKeywordRef = useRef('');
  const resetSearchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const matchedComponentsListRoot = useRef<HTMLDivElement | null>(null);
  const { ref: inViewRef, inView } = useInView({
    threshold: 0.0,
    root: matchedComponentsListRoot.current,
    rootMargin: '0px 0px 200px 0px',
  });
  const closeExpandedComponent = () => setExpandedComponent(null);
  const onSearchKeywordChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value;

    clearResetSearchTimeout();
    setSearchKeyword(nextValue);

    if (nextValue.trim() || !lastSubmittedSearchKeywordRef.current) {
      return;
    }

    resetSearchTimeoutRef.current = setTimeout(() => {
      lastSubmittedSearchKeywordRef.current = '';
      onSearchKeywordChangeProp?.('');
      resetSearchTimeoutRef.current = null;
    }, SEARCH_RESET_DEBOUNCE_MS);
  };

  const clearResetSearchTimeout = () => {
    if (!resetSearchTimeoutRef.current) {
      return;
    }

    clearTimeout(resetSearchTimeoutRef.current);
    resetSearchTimeoutRef.current = null;
  };

  useEffect(() => {
    clearResetSearchTimeout();
    setSearchKeyword('');
    lastSubmittedSearchKeywordRef.current = '';
  }, [selectedSectionDetailInfo]);

  useEffect(() => clearResetSearchTimeout, []);

  const onSearchKeywordEnter = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') {
      return;
    }

    clearResetSearchTimeout();
    lastSubmittedSearchKeywordRef.current = searchKeyword.trim();
    onSearchKeywordChangeProp?.(searchKeyword);
  };

  const showNotEditableMessage =
    !!selectedSectionDetailInfo.length &&
    !isAllowedToAddOverride(selectedSectionDetailInfo);

  const showAddOverrideInfoMessage =
    !!selectedSectionDetailInfo.length &&
    !showNotEditableMessage &&
    !hasClickedAddOverride &&
    !showMatchedComponentsLoader;

  const canShowSearchField =
    !showNotEditableMessage &&
    !showAddOverrideInfoMessage &&
    !isMatchedComponentsLoading;

  const canShowMatchedComponents =
    !showNotEditableMessage &&
    !showAddOverrideInfoMessage &&
    !isMatchedComponentsLoading;
  const handleSelectPromo = () => {
    if (!expandedComponent) {
      return;
    }

    onSelectPromo(expandedComponent);
    closeExpandedComponent();
  };

  useEffect(() => {
    if (
      !inView ||
      isFetchingNextMatchedComponentsPage ||
      !hasNextMatchedComponentsPage
    ) {
      return;
    }
    fetchNextMatchedComponentsPage();
  }, [
    fetchNextMatchedComponentsPage,
    hasNextMatchedComponentsPage,
    inView,
    isFetchingNextMatchedComponentsPage,
  ]);

  const expandedComponentContent = expandedComponent ? (
    <ExpandedComponentModalContent>
      <ExpandedComponentPreview component={expandedComponent} />
      <ExpandedComponentTags>
        {!!expandedComponent.componentTags?.length &&
          expandedComponent.componentTags.map((tag) => (
            <Chip key={tag.id} label={tag.name} showTooltipOnOverflow />
          ))}
      </ExpandedComponentTags>
    </ExpandedComponentModalContent>
  ) : null;

  return (
    <OverrideRightSideBarWrapper>
      <OverrideRightSideBarHeader variant="body2">
        {lang.scheduleComponent.preview.promosAndProducts}
      </OverrideRightSideBarHeader>
      <OverrideRightSideBarContentWrapper>
        {canShowSearchField && (
          <SearchFieldWrapper>
            <SearchField
              fullWidth
              value={searchKeyword}
              placeholder={lang.scheduleComponent.preview.searchPromosAndTags}
              onChange={onSearchKeywordChange}
              onKeyDown={onSearchKeywordEnter}
              searchIcon={<SearchIcon fontSize="inherit" />}
            />
          </SearchFieldWrapper>
        )}
        {showNotEditableMessage && (
          <NotEditableState>
            <NotEditableImage src={NotEditableIcon} alt="" />
            <StyledNotEditableProviderMessage variant="body2">
              {lang.scheduleComponent.preview.selectedComponentNotEditable}
            </StyledNotEditableProviderMessage>
          </NotEditableState>
        )}
        {showAddOverrideInfoMessage && (
          <NotEditableState>
            <NotEditableImage src={ClickPromptIcon} alt="" />
            <StyledEmptyProviderMessage variant="body2">
              {lang.scheduleComponent.preview.addOverrideToModifyContent.line1}
              <br />
              {lang.scheduleComponent.preview.addOverrideToModifyContent.line2}
            </StyledEmptyProviderMessage>
          </NotEditableState>
        )}
        {!showNotEditableMessage &&
          !showAddOverrideInfoMessage &&
          showMatchedComponentsLoader &&
          !isFetchingNextMatchedComponentsPage && (
            <InitialLoadingWrapper>
              <Progress />
            </InitialLoadingWrapper>
          )}
        {canShowMatchedComponents && (
          <>
            <EmptyProvider
              dataToCheck={matchedComponents}
              image={NoValidPromoIcon}
              imageWidth={233}
              imageHeight={211}
              message={
                <StyledEmptyProviderMessage variant="body2">
                  {lang.scheduleComponent.preview.noValidPromotion}
                </StyledEmptyProviderMessage>
              }>
              <>
                <MatchedComponentsList ref={matchedComponentsListRoot}>
                  {matchedComponents.map((component) => (
                    <MiniCardWrapper key={component.id}>
                      <MiniCard
                        imageSlot={renderMiniCardImageSlot()}
                        title={component.name}
                        subLabelText={formatMiniCardDate(component)}
                        subLabelIcon={getMiniCardSubLabelIcon(component)}
                        onCardClick={() => setExpandedComponent(component)}
                        chips={getComponentChipLabels(component)}
                        chipShowCount={1}
                      />
                    </MiniCardWrapper>
                  ))}
                  {hasNextMatchedComponentsPage && (
                    <InViewTrigger ref={inViewRef} />
                  )}
                  {isFetchingNextMatchedComponentsPage && (
                    <LoadingMoreWrapper>
                      <Progress />
                    </LoadingMoreWrapper>
                  )}
                </MatchedComponentsList>
              </>
            </EmptyProvider>
          </>
        )}
      </OverrideRightSideBarContentWrapper>
      <Modal
        open={!!expandedComponent}
        title={expandedComponent?.name || ''}
        subheader={`${lang.scheduleComponent.preview.expires}: ${
          expandedComponent?.end_date
            ? dayjs(expandedComponent.end_date).format(US_DATE_SLASH_FORMAT)
            : lang.scheduleComponent.preview.ongoing
        }`}
        onClose={closeExpandedComponent}
        onCancelAction={closeExpandedComponent}
        onConfirmAction={handleSelectPromo}
        cancelButtonText={lang.close}
        confirmButtonText={lang.scheduleComponent.preview.selectPromo}
        sizeVariant="small"
        content={expandedComponentContent}
        showFooterDivider={false}
      />
    </OverrideRightSideBarWrapper>
  );
};
